import PageContainer from 'components/PageContainer';
import { CircularProgress, Theme, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useGetRole } from 'hooks/ui/useGetRole';
import { useFunds } from 'hooks/data/fund/useFunds';
import PageContent from 'components/PageContent';
import { Link } from 'react-router-dom';
import {
  CheckCircle,
  Cancel,
  Archive,
  Unarchive,
  ContentCopy,
  MailOutline,
  Delete,
  SwapHoriz,
} from '@mui/icons-material';
import {
  numberToCurrencyString,
  dateToLabel,
  formatAbandonmentCart,
} from 'further-ui/utils';
import InvestmentTable from './components/InvestmentTable';
import { TableActions } from 'components/Table';
import { TableAction } from 'components/Table/TableActions';
import { InvestmentFilters } from './components/InvestmentFilters';
import { InvestmentArchiveStatus } from 'further-types/investment';
import { InvestmentCompletionStatus } from 'further-types/investment';
import useUnArchiveIncompleteInvestment from './hooks/useUnArchiveIncompleteInvestment';
import useExcelExport from './hooks/useExcelExport';
import Row from 'components/Layout/Row';
import useIncompleteInvestments from 'hooks/data/investment/useIncompleteInvestments';
import useArchiveIncompleteInvestment from 'hooks/data/investment/useArchiveIncompleteInvestment';
import useResendAdvisedApplicationInvite from 'hooks/data/investment/useResendAdvisedApplicationInvite';
import useGetAdvisedApplicationUrl from 'hooks/data/investment/useGetAdvisedApplicationUrl';
import useChangeApplicationTranche from 'hooks/data/investment/useChangeApplicationTranche';
import useSoftDeleteAdvisedApplication from 'hooks/data/investment/useSoftDeleteAdvisedApplication';
import { useEffect, useState, useMemo } from 'react';
import { useDebounce } from 'usehooks-ts';
import { InvestorLink, AdviserLink } from 'components/Links';
import { DisplayFilterOption, DISPLAY_FILTER_OPTIONS } from './constants';
import { useDisclosure, useFilters, usePagination } from 'further-ui/hooks';
import AlertDialog from 'components/AlertDialog';
import TrancheSelector from 'components/TrancheSelector';
import { RolePermission } from 'further-types/admin-user-role';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { SortOrder } from 'further-types/utils';

const breadcrumbs = [
  { label: 'Dashboard', link: '/' },
  { label: 'Subscription Funnel', isActive: true },
];

const StyledChangeTrancheModalSubtitle = styled('div')(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const getColumns = (
  isSuperAdmin: boolean,
  theme: Theme,
  archiveInvestment: ({ id }: { id: string }) => void,
  unarchiveInvestment: ({ id }: { id: string }) => void,
  setProcessingId: (id: string) => void,
  processingId: string | null,
  resendInvitation?: (id: string) => void,
  copyApplicationUrl?: (id: string) => void,
  changeTranche?: (id: string, firmId: string, fundId?: string) => void,
  deleteApplication?: (id: string) => void,
  isArchived?: (row: any) => boolean,
  hasDeleteInvestmentPermission?: boolean,
  hasEditInvestmentPermission?: boolean,
) => [
  {
    label: 'Investor',
    key: 'fullName',
    sort: true,
    render: (elm) => (
      <InvestorLink
        investorId={elm?.investor?._id}
        investorName={elm?.fullName}
      />
    ),
  },
  {
    label: 'Email',
    key: 'email',
    sort: true,
    render: (elm) => elm?.investor?.email,
  },
  ...(isSuperAdmin
    ? [
        {
          label: 'Firm',
          key: 'firmName',
          sort: true,
          render: (elm) => elm?.firmName,
        },
      ]
    : []),
  {
    label: 'Tranche',
    sort: true,
    key: 'fundName',
    render: (elm) => elm?.fundName,
  },
  {
    label: 'Date',
    key: 'createdAt',
    sort: true,
    render: (elm) => dateToLabel(elm?.createdAt),
  },
  {
    label: 'Exited',
    key: 'step',
    sort: false,
    render: formatAbandonmentCart,
  },
  {
    label: 'Adviser',
    key: 'adviserName',
    sort: true,
    render: (elm) =>
      elm?.adviser ? (
        <AdviserLink
          adviserId={elm.adviser._id}
          adviserName={elm.adviser.fullName}
        />
      ) : (
        'Direct'
      ),
  },
  {
    label: 'Advice fee',
    key: 'adviserFee',
    sort: false,
    render: (elm) =>
      elm?.adviserFee && elm?.adviserFee > 0
        ? numberToCurrencyString(elm.adviserFee)
        : '-',
  },
  {
    label: 'Subscription',
    sort: true,
    key: 'investmentAmount',
    render: (elm) => numberToCurrencyString(elm?.investmentAmount),
  },
  {
    label: 'Completed',
    key: 'completed',
    centered: true,
    sort: false,
    render: (elm) => (
      <Row centered justify="center">
        {elm.subsequentInvestment?._id ? (
          <Link
            to={`/view-investment/${elm.subsequentInvestment._id}`}
            style={{
              color: theme.palette.text.rootColor,
            }}
          >
            <CheckCircle fontSize="small" />
          </Link>
        ) : (
          <Cancel color="error" fontSize="small" />
        )}
      </Row>
    ),
  },
  {
    label: 'Actions',
    key: 'actions',
    sort: false,
    centered: true,
    render: (row) => {
      const isAdvised = !!row.adviserId;
      const archived = isArchived ? isArchived(row) : false;

      if (processingId === row._id) {
        return (
          <Row centered justify="center">
            <CircularProgress size={16} />
          </Row>
        );
      }

      const actions: Array<TableAction> = [];

      if (isAdvised) {
        const isCompleted = !!row?.subsequentInvestment?._id;
        if (resendInvitation) {
          actions.push({
            label: 'Resend application invitation',
            icon: MailOutline,
            onClick: () => {
              resendInvitation(row._id);
            },
          });
        }
        if (copyApplicationUrl) {
          actions.push({
            label: 'Copy application URL',
            icon: ContentCopy,
            onClick: () => {
              copyApplicationUrl(row._id);
            },
          });
        }
        if (changeTranche) {
          actions.push({
            label: 'Change tranche',
            icon: SwapHoriz,
            visible: hasEditInvestmentPermission,
            disabled: isCompleted,
            tooltip: isCompleted
              ? "You can't change the tranche for completed applications."
              : undefined,
            onClick: () => {
              changeTranche(row._id, row.firmId, row.fundId);
            },
          });
        }
        if (deleteApplication) {
          actions.push({
            label: 'Delete',
            icon: Delete,
            visible: hasDeleteInvestmentPermission,
            color: 'error' as const,
            onClick: () => {
              deleteApplication(row._id);
            },
          });
        }
      } else {
        actions.push({
          label: archived ? 'Unarchive record' : 'Archive record',
          tooltip: archived ? 'Unarchive record' : 'Archive record',
          icon: archived ? Unarchive : Archive,
          color: archived ? undefined : ('error' as const),
          visible: hasDeleteInvestmentPermission,
          onClick: () => {
            if (archived) {
              unarchiveInvestment({ id: row._id });
            } else {
              archiveInvestment({ id: row._id });
            }
            setProcessingId(row._id);
          },
        });
      }

      return (
        <Row centered justify="center">
          <TableActions showAsDropdown actions={actions} stopPropagation />
        </Row>
      );
    },
  },
];

const IncompleteInvestments: React.FC = () => {
  const { firmId, isSuperAdmin } = useGetRole();
  const theme = useTheme();
  const [
    hasDeleteInvestmentPermission,
    hasEditInvestmentPermission,
    hasResendInvitesPermission,
  ] = useHasPermissions([
    RolePermission.DeleteIncompleteSubscription,
    RolePermission.EditIncompleteSubscription,
    RolePermission.CreatePlatformInvites,
  ]);
  const {
    orderBy,
    order,
    page,
    rowsPerPage,
    toFirstPage,
    handleRequestSort,
    handleChangePage,
    handleChangeRowsPerPage,
  } = usePagination({
    id: 'incomplete-investments-table',
    orderBy: 'createdAt',
    order: SortOrder.Desc,
  });

  const { filters, handleChangeFilters } = useFilters<{
    fundId: string | null;
    search: string;
    displayFilters: Array<DisplayFilterOption>;
    firmId: string | null;
  }>('incomplete-investments-table', {
    defaultFilters: {
      fundId: null,
      search: '',
      displayFilters: [DisplayFilterOption.ShowUnarchived],
      firmId: null,
    },
    onFiltersChange: () => toFirstPage(),
  });

  // Apply fundId from URL when navigating from e.g. tranche dashboard link
  useEffect(() => {
    const qs = new URLSearchParams(window.location.search);
    const fundIdParam = qs.get('fundId');
    if (fundIdParam) {
      handleChangeFilters({ fundId: fundIdParam });
    }
  }, []);

  const {
    mutate: archiveInvestment,
    isError: archiveError,
    isPending: isArchivePending,
  } = useArchiveIncompleteInvestment();
  const {
    mutate: unarchiveInvestment,
    isError: unarchiveError,
    isPending: isUnarchivePending,
  } = useUnArchiveIncompleteInvestment();

  const [processingId, setProcessingId] = useState<string | null>(null);

  // Use firmId from filters if super admin, otherwise use logged-in user's firmId
  const effectiveFirmId = filters.firmId || firmId || null;
  const { tranches } = useFunds({ firmId: effectiveFirmId || undefined });

  const [searchTerm, setSearchTerm] = useState(filters.search);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Map displayFilters to status and archiveStatus for API
  const { status, archiveStatus } = useMemo(() => {
    let computedStatus = InvestmentCompletionStatus.All;
    let computedArchiveStatus = InvestmentArchiveStatus.All;

    // If all filters are selected or no filters selected, show all (no filtering)
    const hasAllFilters =
      filters.displayFilters.length === 0 ||
      filters.displayFilters.length === DISPLAY_FILTER_OPTIONS.length;

    if (!hasAllFilters) {
      // Archive status
      if (
        filters.displayFilters.includes(DisplayFilterOption.ShowUnarchived) &&
        !filters.displayFilters.includes(DisplayFilterOption.ShowArchived)
      ) {
        computedArchiveStatus = InvestmentArchiveStatus.NotArchived;
      } else if (
        filters.displayFilters.includes(DisplayFilterOption.ShowArchived) &&
        !filters.displayFilters.includes(DisplayFilterOption.ShowUnarchived)
      ) {
        computedArchiveStatus = InvestmentArchiveStatus.Archived;
      }

      // Completion status
      if (
        filters.displayFilters.includes(DisplayFilterOption.ShowComplete) &&
        !filters.displayFilters.includes(DisplayFilterOption.ShowIncomplete)
      ) {
        computedStatus = InvestmentCompletionStatus.Completed;
      } else if (
        filters.displayFilters.includes(DisplayFilterOption.ShowIncomplete) &&
        !filters.displayFilters.includes(DisplayFilterOption.ShowComplete)
      ) {
        computedStatus = InvestmentCompletionStatus.Incomplete;
      }
    }

    return { status: computedStatus, archiveStatus: computedArchiveStatus };
  }, [filters.displayFilters]);

  const {
    data: incompleteInvestments,
    isLoading,
    isFetching,
  } = useIncompleteInvestments({
    page: page.toString(),
    firmId: effectiveFirmId || '',
    fundId: filters.fundId || '',
    perPage: rowsPerPage.toString(),
    order,
    orderBy,
    search: debouncedSearchTerm,
    status,
    archiveStatus,
    showAdvisedOnly:
      filters.displayFilters.includes(
        DisplayFilterOption.ShowAdvisedApplications,
      ) && filters.displayFilters.length !== DISPLAY_FILTER_OPTIONS.length,
  });

  useEffect(() => {
    if (
      (!isArchivePending && !isUnarchivePending && processingId) ||
      archiveError ||
      unarchiveError
    ) {
      setProcessingId(null);
    }
  }, [
    isArchivePending,
    isUnarchivePending,
    processingId,
    archiveError,
    unarchiveError,
  ]);

  const { exportExcelSheet, isExporting } = useExcelExport({
    firmId: effectiveFirmId || '',
    fundId: filters.fundId || '',
    order,
    orderBy,
    search: filters.search,
    status,
    archiveStatus,
    isExport: true,
  });

  const resendInvitationMutation = useResendAdvisedApplicationInvite();
  const copyUrlMutation = useGetAdvisedApplicationUrl();
  const changeTrancheMutation = useChangeApplicationTranche();
  const deleteApplicationMutation = useSoftDeleteAdvisedApplication();
  const changeTrancheDisclosure = useDisclosure();
  const deleteDisclosure = useDisclosure();

  const handleResendInvitation = (id: string) => {
    resendInvitationMutation.mutate({ id });
  };

  const handleCopyApplicationUrl = (id: string) => {
    copyUrlMutation.mutate(id);
  };

  const handleChangeTranche = (id: string, firmId: string, fundId?: string) => {
    changeTrancheDisclosure.stageAction({ id, firmId, fundId });
  };

  const handleCloseChangeTranche = () => {
    changeTrancheDisclosure.onClose();
  };

  const handleConfirmChangeTranche = () => {
    const { id, fundId } = changeTrancheDisclosure.actionProps;
    if (id) {
      changeTrancheMutation.mutate({
        id,
        fundId,
      });
      changeTrancheDisclosure.onClose();
    }
  };

  const handleDeleteApplication = (id: string) => {
    deleteDisclosure.stageAction({ id });
  };

  const handleConfirmDelete = () => {
    const { id } = deleteDisclosure.actionProps;
    if (id) {
      deleteApplicationMutation.mutate({ id });
      deleteDisclosure.onClose();
    }
  };

  const checkIsArchived = (row: any) => {
    // Check admin archive flag or legacy status
    return (
      !!row.archivedForAdminAt ||
      row.status === InvestmentArchiveStatus.Archived
    );
  };

  const columns = getColumns(
    isSuperAdmin,
    theme,
    archiveInvestment,
    unarchiveInvestment,
    setProcessingId,
    processingId,
    hasResendInvitesPermission ? handleResendInvitation : undefined,
    handleCopyApplicationUrl,
    hasEditInvestmentPermission ? handleChangeTranche : undefined,
    hasDeleteInvestmentPermission ? handleDeleteApplication : undefined,
    checkIsArchived,
    hasDeleteInvestmentPermission,
    hasEditInvestmentPermission,
  );

  const shouldReload = isFetching && !processingId;

  const handleFiltersChange = (newFilters: Partial<typeof filters>) => {
    if ('search' in newFilters) {
      setSearchTerm(newFilters.search ?? '');
    }
    handleChangeFilters(newFilters);
  };

  return (
    <PageContainer heading="Subscription Funnel" breadcrumbs={breadcrumbs}>
      <PageContent>
        <InvestmentFilters
          exportExcelSheet={exportExcelSheet}
          isExporting={isExporting}
          filters={{
            ...filters,
            search: searchTerm,
          }}
          handleChangeFilters={handleFiltersChange}
          tranchesList={tranches.data}
        />
        <InvestmentTable
          columns={columns}
          data={
            incompleteInvestments || {
              result: [],
              total: 0,
              perPage: rowsPerPage,
              totalInvestment: 0,
            }
          }
          paginationProps={{
            order,
            orderBy,
            page,
            rowsPerPage,
            handleChangePage,
            handleChangeRowsPerPage,
            handleRequestSort,
          }}
          isLoading={isLoading || shouldReload}
        />
        <AlertDialog
          open={changeTrancheDisclosure.isOpen}
          onClose={handleCloseChangeTranche}
          title="Change tranche"
          content={
            <>
              <StyledChangeTrancheModalSubtitle>
                Select a new tranche for this advised application.
              </StyledChangeTrancheModalSubtitle>
              <TrancheSelector
                firmId={changeTrancheDisclosure.actionProps.firmId ?? undefined}
                openOnly
                selectedTranche={
                  changeTrancheDisclosure.actionProps.fundId && tranches.data
                    ? tranches.data.find(
                        ({ _id }) =>
                          _id === changeTrancheDisclosure.actionProps.fundId,
                      ) ?? null
                    : null
                }
                onChange={(tranche) => {
                  changeTrancheDisclosure.stageAction({
                    ...changeTrancheDisclosure.actionProps,
                    fundId: tranche?._id ?? null,
                  });
                }}
              />
            </>
          }
          btnLabels={{
            cancel: 'Go back',
            confirm: 'Save',
          }}
          onConfirm={handleConfirmChangeTranche}
          confirmBtnProps={{
            disabled:
              !changeTrancheDisclosure.actionProps.fundId ||
              changeTrancheMutation.isPending,
            loading: changeTrancheMutation.isPending,
          }}
        />
        <AlertDialog
          open={deleteDisclosure.isOpen}
          onClose={deleteDisclosure.onClose}
          title="Delete application"
          content="Are you sure you want to delete this application? This action cannot be undone."
          btnLabels={{
            cancel: 'Go back',
            confirm: 'Delete',
          }}
          onConfirm={handleConfirmDelete}
          confirmBtnProps={{
            disabled: deleteApplicationMutation.isPending,
            loading: deleteApplicationMutation.isPending,
          }}
        />
      </PageContent>
    </PageContainer>
  );
};

export default IncompleteInvestments;
